import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {FONT_PROXIMA_12} from 'styles/FontConstants';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GREEN, NEAR_BLACK, ORANGE} from 'ui-library/Colors';
import ErrorMessage from 'ui-library/ErrorMessage';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import TextInput from 'ui-library/inputs/TextInput';
import ReorderableSimpleTable from 'ui-library/reorderable/ReorderableSimpleTable';
import {Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';
import * as keyboard from 'util/Keyboard';
import * as modals from 'util/Modals';
import {isAbortError} from 'util/Net';
import {ElementKeyboardEvent} from 'util/Types';

import {BartenderServer, fetchBartenderLabelTemplates, fetchBartenderPrinters} from './BartenderUtil';

interface BartenderServerStatusInfo {
  numLabelTemplates?: number;
  numPrinters?: number;
  error?: string;
}

function BartenderServerStatus(props: {status?: BartenderServerStatusInfo}): JSX.Element {
  if (props.status?.numLabelTemplates != null && props.status?.numPrinters != null) {
    return (
      <div>
        <BenchlingIcon className={css(styles.green)} icon="dot" /> Online
      </div>
    );
  } else if (props.status?.error != null) {
    return (
      <>
        <div>
          <BenchlingIcon className={css(styles.orange)} icon="warning" /> Error
        </div>
        <div className={css(styles.errorMessage)}>{props.status?.error.replace(/^\w*Error: /, '')}</div>
      </>
    );
  } else {
    return <HorizontalThrobber />;
  }
}

interface Props {
  bartenderServers: Array<BartenderServer>;
  isWritable: boolean;
  onChange: (newBartenderServers: Array<BartenderServer>) => void;
}

interface EditingBartenderServer {
  typename: 'EditingBartenderServer'; // to easily distinguish
  url: string | null; // null when adding a new server
  newUrl: string;
  newName: string;
}

function isEditingBartenderServer(
  bs: EditingBartenderServer | BartenderServer
): bs is EditingBartenderServer {
  return (bs as EditingBartenderServer).typename === 'EditingBartenderServer';
}

export default function BartenderServerTable(props: Props): JSX.Element {
  const [bartenderServerStatus, setBartenderServerStatus] = useState<{
    [bartenderUrl: string]: BartenderServerStatusInfo;
  }>({});
  // For simplicity we allow editing only one BarTender server at a time, which can be either an existing
  // server (by clicking the pen icon) or a new server (by clicking the plus icon). We know it's a new server
  // when `editingBartenderServer.url` is null.
  const [editingBartenderServer, setEditingBartenderServer] = useState<EditingBartenderServer | null>(null);
  const [hasSubmittedInvalidName, setHasSubmittedInvalidName] = useState<boolean>(false);
  const [hasSubmittedInvalidUrl, setHasSubmittedInvalidUrl] = useState<boolean>(false);

  useEffect((): (() => void) => {
    const abortController = new AbortController();
    let isMounted = true;
    for (const bartenderServer of props.bartenderServers) {
      fetchBartenderPrinters(bartenderServer.url, abortController)
        // eslint-disable-next-line no-loop-func
        .then((printers) => {
          if (isMounted) {
            setBartenderServerStatus((prevStatus) => ({
              ...prevStatus,
              [bartenderServer.url]: {
                ...prevStatus[bartenderServer.url],
                numPrinters: printers.filter((p) => p !== 'PDF').length,
              },
            }));
          }
        })
        // eslint-disable-next-line no-loop-func
        .catch((reason: unknown) => {
          if (isMounted && !isAbortError(reason)) {
            setBartenderServerStatus((prevStatus) => ({
              ...prevStatus,
              [bartenderServer.url]: {
                ...prevStatus[bartenderServer.url],
                error: String(reason).toString(),
              },
            }));
          }
        });
    }

    return function cleanup() {
      // Avoid "Warning: Can't perform a React state update on an unmounted component...."
      isMounted = false;
      abortController.abort();
    };
  }, [props.bartenderServers]);

  useEffect((): (() => void) => {
    const abortController = new AbortController();
    let isMounted = true;
    for (const bartenderServer of props.bartenderServers) {
      fetchBartenderLabelTemplates(bartenderServer.url, abortController)
        // eslint-disable-next-line no-loop-func
        .then((labelTemplates) => {
          if (isMounted) {
            setBartenderServerStatus((prevStatus) => ({
              ...prevStatus,
              [bartenderServer.url]: {
                ...prevStatus[bartenderServer.url],
                numLabelTemplates: labelTemplates.length,
              },
            }));
          }
        })
        // eslint-disable-next-line no-loop-func
        .catch((reason: unknown) => {
          if (isMounted && !isAbortError(reason)) {
            setBartenderServerStatus((prevStatus) => ({
              ...prevStatus,
              [bartenderServer.url]: {
                ...prevStatus[bartenderServer.url],
                error: String(reason).toString(),
              },
            }));
          }
        });
    }

    return function cleanup() {
      // Avoid "Warning: Can't perform a React state update on an unmounted component...."
      isMounted = false;
      abortController.abort();
    };
  }, [props.bartenderServers]);

  const handleClickAdd = (): void => {
    if (editingBartenderServer == null) {
      setEditingBartenderServer({typename: 'EditingBartenderServer', url: null, newUrl: '', newName: ''});
    }
  };

  const handleClickEdit = (bartenderServer: BartenderServer): void => {
    if (editingBartenderServer == null) {
      setEditingBartenderServer({
        typename: 'EditingBartenderServer',
        url: bartenderServer.url,
        newUrl: bartenderServer.url,
        newName: bartenderServer.name ?? '',
      });
    }
  };

  const handleClickDelete = (bartenderServerUrl: string): void => {
    modals
      .confirm('Delete print server?', `You won't be able to undo this action.`, ButtonStyle.PRIMARY, {
        acceptText: 'Delete',
        enableRedesign: true,
      })
      .done(() => {
        props.onChange(
          props.bartenderServers.filter((bartenderServer) => bartenderServer.url !== bartenderServerUrl)
        );
      });
  };

  const handleReorderRow = (bartenderServerUrl: string, newIndex: number): void => {
    const tmp = [...props.bartenderServers];
    const oldIndex = props.bartenderServers.findIndex((bs) => bs.url === bartenderServerUrl);
    const bartenderServerToMove = props.bartenderServers[oldIndex];
    tmp.splice(oldIndex, 1); // remove from old index
    tmp.splice(newIndex, 0, bartenderServerToMove); // add at new index
    props.onChange(tmp);
  };

  const getViewModeRow = (bartenderServer: BartenderServer): Row => ({
    id: bartenderServer.url,
    cells: [
      {key: `${bartenderServer.url}:url`, contents: <>{bartenderServer.url}</>},
      {key: `${bartenderServer.url}:name`, contents: <>{bartenderServer.name}</>},
      {
        key: `${bartenderServer.url}:status`,
        contents: <BartenderServerStatus status={bartenderServerStatus[bartenderServer.url]} />,
        cellClassName: 'simpleDivTable-cell--narrow',
      },
      {
        key: `${bartenderServer.url}:printers`,
        contents: <>{bartenderServerStatus[bartenderServer.url]?.numPrinters}</>,
        cellClassName: 'simpleDivTable-cell--narrow',
      },
      {
        key: `${bartenderServer.url}:labelTemplates`,
        contents: <>{bartenderServerStatus[bartenderServer.url]?.numLabelTemplates}</>,
        cellClassName: 'simpleDivTable-cell--narrow',
      },
    ],
    actionGroup:
      props.isWritable && editingBartenderServer == null ? (
        <div className="registry-actionGroup">
          <Tooltip container="body" placement={TooltipPlacement.TOP} title="Delete">
            <span
              className={classNames(css(styles.singleButton), 'metadata-action faded u-danger-pointer')}
              onClick={() => {
                handleClickDelete(bartenderServer.url);
              }}
            >
              <BenchlingIcon icon="delete" />
            </span>
          </Tooltip>
          <Tooltip container="body" placement={TooltipPlacement.TOP} title="Edit">
            <span
              className={classNames(css(styles.singleButton), 'metadata-action faded u-primary-pointer')}
              onClick={() => {
                handleClickEdit(bartenderServer);
              }}
            >
              <BenchlingIcon icon="edit" />
            </span>
          </Tooltip>
        </div>
      ) : null,
  });

  const handleClickCancel = (): void => {
    setEditingBartenderServer(null);
    setHasSubmittedInvalidName(false);
    setHasSubmittedInvalidUrl(false);
  };

  const urlIsNonEmpty = (): boolean =>
    editingBartenderServer != null && editingBartenderServer?.newUrl.trim() !== '';

  const urlIsDuplicate = (): boolean =>
    props.bartenderServers
      .filter((bs) => bs.url !== editingBartenderServer?.url)
      .some((bs) => bs.url === editingBartenderServer?.newUrl.trim());

  const urlError = (): string | null =>
    !urlIsNonEmpty() ? 'URL cannot be empty' : urlIsDuplicate() ? 'URL already in use' : null;

  /**
   * @returns true if new url is not empty and not a duplicate
   */
  const isNewUrlValid = (): boolean => urlIsNonEmpty() && !urlIsDuplicate();

  const nameIsDuplicate = (): boolean =>
    props.bartenderServers
      .filter((bs) => bs.url !== editingBartenderServer?.url)
      .some((bs) => bs.name === editingBartenderServer?.newName.trim());

  const nameError = (): string | null => (nameIsDuplicate() ? 'Name already in use' : null);

  /**
   * @returns true if new name not a duplicate
   */
  const isNewNameValid = (): boolean =>
    editingBartenderServer != null && (editingBartenderServer?.newName.trim() === '' || !nameIsDuplicate());

  /**
   * @returns true if BarTender server currently being edited is valid to save
   */
  const isValidEdit = (): boolean => isNewUrlValid() && isNewNameValid();

  const handleClickSave = (): void => {
    const index = props.bartenderServers.findIndex((bs) => bs.url === editingBartenderServer?.url);
    if (!isValidEdit()) {
      if (!isNewUrlValid()) {
        setHasSubmittedInvalidUrl(true);
      }
      if (!isNewNameValid()) {
        setHasSubmittedInvalidName(true);
      }
      return;
    } else {
      setHasSubmittedInvalidName(false);
      setHasSubmittedInvalidUrl(false);
    }
    const newOrUpdatedServer = {
      url: editingBartenderServer!.newUrl.trim(),
      name: editingBartenderServer!.newName.trim() || null,
    };

    const tmp = [...props.bartenderServers];
    if (index >= 0) {
      tmp[index] = newOrUpdatedServer;
    } else {
      tmp.push(newOrUpdatedServer);
    }

    setEditingBartenderServer(null);
    props.onChange(tmp);
  };

  const handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (keyboard.isEnterKey(e)) {
      e.preventDefault();
      handleClickSave();
    } else if (keyboard.isEscapeKey(e)) {
      e.preventDefault();
      handleClickCancel();
    }
  };

  const getEditModeRow = (ebs: EditingBartenderServer): Row => ({
    id: 'editingBartenderServer',
    cells: [
      {
        key: 'editingBartenderServer:newUrl',
        contents: (
          <>
            <TextInput
              value={ebs.newUrl}
              onChange={(e) => {
                setEditingBartenderServer({...editingBartenderServer!, newUrl: e.target.value});
              }}
              placeholder="URL"
              autoFocus={true}
              hasError={!isNewUrlValid()}
              onKeyDown={handleKeyDown}
            />
            {hasSubmittedInvalidUrl && !isNewUrlValid() && <ErrorMessage error={urlError()} />}
          </>
        ),
      },
      {
        key: 'editingBartenderServer:newName',
        contents: (
          <>
            <TextInput
              value={ebs.newName}
              onChange={(e) => {
                setEditingBartenderServer({...editingBartenderServer!, newName: e.target.value});
              }}
              placeholder="Display name (optional)"
              hasError={!isNewNameValid()}
              onKeyDown={handleKeyDown}
            />
            {hasSubmittedInvalidName && !isNewNameValid() && <ErrorMessage error={nameError()} />}
          </>
        ),
      },
      {
        key: 'editingBartenderServer:status',
        contents: ebs.url != null ? <BartenderServerStatus status={bartenderServerStatus[ebs.url]} /> : '',
        cellClassName: 'simpleDivTable-cell--narrow',
        contentsClassName: css(styles.faded),
      },
      {
        key: 'editingBartenderServer:printers',
        contents: ebs.url != null ? <>{bartenderServerStatus[ebs.url]?.numPrinters}</> : '',
        cellClassName: 'simpleDivTable-cell--narrow',
        contentsClassName: css(styles.faded),
      },
      {
        key: 'editingBartenderServer:labelTemplates',
        contents: ebs.url != null ? <>{bartenderServerStatus[ebs.url]?.numLabelTemplates}</> : '',
        cellClassName: 'simpleDivTable-cell--narrow',
        contentsClassName: css(styles.faded),
      },
    ],
    actionGroup: (
      <div className={classNames('registry-actionGroup', css(styles.alwaysVisible))}>
        <Tooltip container="body" placement={TooltipPlacement.TOP} title="Cancel">
          <span
            className={classNames(css(styles.singleButton), 'metadata-action faded u-danger-pointer')}
            onClick={handleClickCancel}
          >
            <BenchlingIcon className="metadata-action-icon--close" icon="close" />
          </span>
        </Tooltip>
        <Tooltip container="body" placement={TooltipPlacement.TOP} title="Save">
          <span
            className={classNames(css(styles.singleButton), 'metadata-action faded u-primary-pointer')}
            onClick={handleClickSave}
          >
            <BenchlingIcon className={classNames('metadata-action-icon--complete')} icon="check" />
          </span>
        </Tooltip>
      </div>
    ),
    rowClassName: css(styles.alignItemsTop),
  });

  const getRows = (): Array<Row> => {
    const rowInfos: Array<EditingBartenderServer | BartenderServer> = [
      ...props.bartenderServers.map((bs) =>
        editingBartenderServer?.url === bs.url ? editingBartenderServer : bs
      ),
      ...(editingBartenderServer != null && editingBartenderServer.url == null
        ? [editingBartenderServer]
        : []),
    ];
    return rowInfos.map((bartenderServer): Row =>
      isEditingBartenderServer(bartenderServer)
        ? getEditModeRow(bartenderServer)
        : getViewModeRow(bartenderServer)
    );
  };

  return (
    <ReorderableSimpleTable
      headerCells={[
        {
          key: 'url',
          contents: (
            <>
              BarTender servers
              {props.isWritable && (
                <BenchlingIcon
                  className={classNames({
                    'simpleDivTable-addButton': true,
                    'simpleDivTable-addButton--disabled': editingBartenderServer != null,
                  })}
                  icon="compose"
                  onClick={handleClickAdd}
                />
              )}
            </>
          ),
          contentsClassName: css(styles.nearBlack),
        },
        {key: 'name', contents: <>Display name</>},
        {key: 'status', contents: <>Status</>, cellClassName: 'simpleDivTable-cell--narrow'},
        {key: 'printers', contents: <>Printers</>, cellClassName: 'simpleDivTable-cell--narrow'},
        {
          key: 'labelTemplates',
          contents: <>Label templates</>,
          cellClassName: 'simpleDivTable-cell--narrow',
        },
      ]}
      isDraggingDisabled={!props.isWritable || editingBartenderServer != null}
      onReorderRow={handleReorderRow}
      noRowsDisplay="You don't have any BarTender servers configured."
      rows={getRows()}
      dataTestAttrs={{component: 'BartenderServerTable', element: 'root'}}
    />
  );
}

export const styles = StyleSheet.create({
  nearBlack: {color: NEAR_BLACK},
  orange: {color: ORANGE},
  green: {color: GREEN},
  singleButton: {marginLeft: '6px', marginRight: '6px', padding: 0},
  faded: {opacity: 0.5},
  alwaysVisible: {visibility: 'visible'},
  alignItemsTop: {alignItems: 'flex-start'},
  errorMessage: {
    ...FONT_PROXIMA_12,
    whiteSpace: 'normal',
  },
});
